import type { ReactNode } from 'react'
import { IconButton } from '@/components/IconButton'
import {
  EditorList,
  EditorListBadge,
  EditorListItemActions,
  EditorListItemContent,
  EditorListItemDescription,
  EditorListItemTitle,
  SortableEditorListItem,
} from '@/features/legends/components/EditorList'
import { PageIntro } from '@/features/legends/components/PageIntro'
import type { InfoLineDesignee } from '@/features/legends/options/legend-options'
import { match } from '@/utils/pattern-match-util'
import styles from './InfoLines.module.css'

export interface InfoLinesProps {
  /**
   * `InfoLineRow`s.
   */
  children?: ReactNode
  onAdd: () => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * The legend's info lines: extra context shown on the job.
 */
export function InfoLines({
  children,
  onAdd,
  isDisabled,
  testId,
}: InfoLinesProps) {
  return (
    <div className={styles.root}>
      <PageIntro title="Info lines">
        Add textual context based on answers, formulas and more.
      </PageIntro>
      <EditorList
        onAdd={onAdd}
        addLabel="Add info line"
        isDisabled={isDisabled}
        testId={testId}
      >
        {children}
      </EditorList>
    </div>
  )
}

const lineGroup = Symbol('info-line')

export interface InfoLineRowProps {
  index: number
  onMove: (fromIndex: number, toIndex: number) => unknown
  title: string
  /**
   * The content formula's last expression.
   */
  summary: string
  designee: InfoLineDesignee
  isSelected?: boolean
  hasError?: boolean
  isDisabled?: boolean
  onPress: () => unknown
  onRemove: () => unknown
  /**
   * The remove button gets `${testId}-remove-button`.
   */
  testId?: string
}

/**
 * An info line in {@link InfoLines}, which can be dragged to reorder.
 */
export function InfoLineRow({
  index,
  onMove,
  title,
  summary,
  designee,
  isSelected,
  hasError,
  isDisabled,
  onPress,
  onRemove,
  testId,
}: InfoLineRowProps) {
  return (
    <SortableEditorListItem
      group={lineGroup}
      index={index}
      onMove={onMove}
      isDisabled={isDisabled}
      isSelected={isSelected}
      hasError={hasError}
      onPress={onPress}
      testId={testId}
    >
      <div className={styles.row}>
        <EditorListItemContent>
          <EditorListItemTitle isMuted={!title}>
            {title || 'No title'}
          </EditorListItemTitle>
          <EditorListItemDescription isMuted={!summary}>
            {summary || 'No content formula.'}
          </EditorListItemDescription>
        </EditorListItemContent>
        <EditorListItemActions>
          <EditorListBadge
            variant={match(designee, {
              Client: 'primary',
              Provider: 'secondary',
              Both: 'default',
            })}
          >
            {designee}
          </EditorListBadge>
          {!isDisabled && (
            <IconButton
              name="close"
              label="Remove line"
              size={14}
              onPress={onRemove}
              testId={testId && `${testId}-remove-button`}
            />
          )}
        </EditorListItemActions>
      </div>
    </SortableEditorListItem>
  )
}
